import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TimePicker as MUITimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';
import ReadOnly from './ReadOnly';

/**
 * Converts a "HH:mm" 24-hour time string to "hh:mm A" 12-hour format using dayjs.
 * @param {string} time24 - Time string in "HH:mm" format (e.g., "13:45").
 * @returns {string} Time in "hh:mm A" format (e.g., "01:45 PM").
 */
export function formatToAmPm(time24) {
  try {
    return dayjs(time24, 'HH:mm').format('hh:mma');
  } catch {
    return time24;
  }
}

const TimePicker = forwardRef(
  (
    {
      onChange,
      error,
      helperText,
      value,
      format = 'hh:mm A',
      ampm = true,
      ...props
    },
    ref,
  ) => {
    const { getValues } = useFormContext();
    const { isHidden, isReadOnly } = useEntityPermission();
    const [clearTick, setClearTick] = useState(0);
    const prevValueRef = useRef(value);

    const currentvalue = getValues(props?.name);

    useEffect(() => {
      const nowEmpty = value == null || value === '';
      const hadValue =
        prevValueRef.current != null && prevValueRef.current !== '';
      if (nowEmpty && hadValue) {
        setClearTick(n => n + 1);
      }
      prevValueRef.current = value;
    }, [value]);

    if (isReadOnly(props?.permission, currentvalue)) {
      return (
        <ReadOnly name={props?.name}>
          {currentvalue ? formatToAmPm(currentvalue) : ''}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={props?.name} value={currentvalue} />;
    }

    return (
      <MUITimePicker
        key={`${props?.name ?? 'time'}-${clearTick}`}
        fullWidth
        {...props}
        value={value ? dayjs(value, 'HH:mm') : null}
        ampm={ampm}
        format={format}
        onChange={e => {
          onChange({
            target: {
              name: props?.name,
              value: e ? dayjs(e).format('HH:mm') : null,
            },
          });
        }}
        slotProps={{
          textField: {
            inputRef: ref,
            size: 'small',
            variant: 'outlined',
            fullWidth: true,
            autoComplete: 'off',
            error: Boolean(error),
            helperText,
          },
          field: {
            clearable: true,
            onClear: () => {
              onChange({
                target: {
                  name: props?.name,
                  value: null,
                },
              });
            },
          },
        }}
        sx={{
          '& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer':
            {
              padding: '6px 0px 6px 6px!important',
            },
          minWidth: '100%',
          ...(props?.sx || {}),
        }}
      />
    );
  },
);

export default TimePicker;
